import React, { useState, useCallback, useEffect } from "react";
import apiClient from "../../../../../api/apiClient";
import Loading from "../../../../../components/Loading/Loading";

// ── SVG Icons ────────────────────────────────────────────────────────────────

const EmptyStateIcon = () => (
    <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg"
        className="crm-inbox-empty__icon">
        {/* Inbox tray */}
        <rect x="12" y="62" width="72" height="22" rx="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>
        <path d="M12 74h18l6 10h24l6-10h18" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" fill="none"/>
        {/* Sleeping face */}
        <circle cx="48" cy="38" r="20" stroke="currentColor" strokeWidth="2.5" fill="none"/>
        {/* Closed/droopy eyes */}
        <path d="M38 36 Q40 38 42 36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <path d="M54 36 Q56 38 58 36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
        {/* Flat mouth */}
        <path d="M43 44 Q48 46 53 44" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
        {/* Z z z */}
        <text x="70" y="24" fontSize="9" fontWeight="700" fill="currentColor" opacity="0.5">z</text>
        <text x="76" y="16" fontSize="7" fontWeight="700" fill="currentColor" opacity="0.35">z</text>
        <text x="81" y="10" fontSize="5" fontWeight="700" fill="currentColor" opacity="0.2">z</text>
    </svg>
);

const RefreshIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);

const CloseConvIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const ReopenIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
        <polyline points="3 3 3 8 8 8"/>
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(dt) {
    if (!dt) return "—";
    const d = new Date(dt);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86_400_000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ── Component ────────────────────────────────────────────────────────────────

const ConversationInbox = ({ onOpen, admins }) => {
    const [conversations, setConversations] = useState([]);
    const [total, setTotal]               = useState(0);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState("");
    const [statusFilter, setStatusFilter] = useState("open");
    const [selected, setSelected]         = useState(new Set());
    const [pinning, setPinning]           = useState(null);
    const [toggling, setToggling]         = useState(null);
    const [deleting, setDeleting]         = useState(null);

    const fetchConversations = useCallback(async () => {
        try {
            const res = await apiClient.get(`/admin/crm/conversations?limit=100&status=${statusFilter}`);
            setConversations(res.data.conversations);
            setTotal(res.data.total);
            setError("");
        } catch {
            setError("Failed to load conversations.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        setLoading(true);
        fetchConversations();
    }, [fetchConversations]);

    useEffect(() => {
        const timer = setInterval(fetchConversations, 30_000);
        return () => clearInterval(timer);
    }, [fetchConversations]);

    const handleTogglePin = async (e, cv) => {
        e.stopPropagation();
        setPinning(cv.id);
        try {
            await apiClient.patch(`/admin/crm/conversations/${cv.id}`, { pinned: cv.pinned ? 0 : 1 });
            setConversations(prev =>
                prev.map(c => c.id === cv.id ? { ...c, pinned: c.pinned ? 0 : 1 } : c)
            );
        } catch {}
        setPinning(null);
    };

    const handleToggleStatus = async (e, cv) => {
        e.stopPropagation();
        const newStatus = cv.status === "open" ? "closed" : "open";
        setToggling(cv.id);
        try {
            await apiClient.patch(`/admin/crm/conversations/${cv.id}`, { status: newStatus });
            setConversations(prev =>
                prev.map(c => c.id === cv.id ? { ...c, status: newStatus } : c)
            );
        } catch {}
        setToggling(null);
    };

    const handleDelete = async (e, cvId) => {
        e.stopPropagation();
        if (!window.confirm("Delete this conversation and all its messages? This cannot be undone.")) return;
        setDeleting(cvId);
        try {
            await apiClient.delete(`/admin/crm/conversations/${cvId}`);
            setConversations(prev => prev.filter(c => c.id !== cvId));
            setTotal(prev => prev - 1);
            setSelected(prev => { const s = new Set(prev); s.delete(cvId); return s; });
        } catch {}
        setDeleting(null);
    };

    const handleBulkToggleStatus = async () => {
        const newStatus = statusFilter === "open" ? "closed" : "open";
        const ids = [...selected];
        await Promise.all(
            ids.map(id => apiClient.patch(`/admin/crm/conversations/${id}`, { status: newStatus }).catch(() => {}))
        );
        setConversations(prev =>
            prev.map(c => selected.has(c.id) ? { ...c, status: newStatus } : c)
        );
        setSelected(new Set());
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`Delete ${selected.size} conversation${selected.size !== 1 ? "s" : ""} and all their messages? This cannot be undone.`)) return;
        const ids = [...selected];
        await Promise.all(
            ids.map(id => apiClient.delete(`/admin/crm/conversations/${id}`).catch(() => {}))
        );
        setConversations(prev => prev.filter(c => !selected.has(c.id)));
        setTotal(prev => prev - ids.length);
        setSelected(new Set());
    };

    const handleCheckbox = (e, id) => {
        e.stopPropagation();
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = (e) => {
        e.stopPropagation();
        if (selected.size === sorted.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(sorted.map(c => c.id)));
        }
    };

    // Pinned rows float to the top; otherwise keep server order (pinned DESC, lastMessageAt DESC)
    const sorted = [...conversations].sort((a, b) => {
        if ((b.pinned || 0) !== (a.pinned || 0)) return (b.pinned || 0) - (a.pinned || 0);
        return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
    });

    const allSelected = sorted.length > 0 && selected.size === sorted.length;

    return (
        <div className="crm-inbox-root">
            {/* ── Toolbar ── */}
            <div className="crm-inbox-toolbar">
                <div className="crm-inbox-toolbar__left">
                    <span className="crm-inbox-count">
                        {total} conversation{total !== 1 ? "s" : ""}
                    </span>
                    {selected.size > 0 && (
                        <>
                            <span className="crm-inbox-bulk-sep" />
                            <span className="crm-inbox-bulk-label">{selected.size} selected</span>
                            {statusFilter === "open" && (
                                <button
                                    className="crm-inbox-icon-btn crm-inbox-icon-btn--bulk"
                                    onClick={handleBulkToggleStatus}
                                    title="Close selected conversations"
                                >
                                    <CloseConvIcon />
                                </button>
                            )}
                            {statusFilter === "closed" && (
                                <button
                                    className="crm-inbox-icon-btn crm-inbox-icon-btn--bulk"
                                    onClick={handleBulkToggleStatus}
                                    title="Reopen selected conversations"
                                >
                                    <ReopenIcon />
                                </button>
                            )}
                            <button
                                className="crm-inbox-icon-btn crm-inbox-icon-btn--bulk crm-inbox-icon-btn--danger"
                                onClick={handleBulkDelete}
                                title="Delete selected conversations"
                            >
                                <TrashIcon />
                            </button>
                        </>
                    )}
                </div>
                <div className="crm-inbox-toolbar__right">
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setLoading(true); }}
                        className="crm-inbox-select"
                    >
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                        <option value="all">All</option>
                    </select>
                    <button
                        className="crm-inbox-icon-btn"
                        onClick={fetchConversations}
                        title="Refresh"
                    >
                        <RefreshIcon />
                    </button>
                </div>
            </div>

            {error && (
                <div className="admin-form-error" style={{ margin: "0 var(--space-6) var(--space-3)" }}>
                    {error}
                </div>
            )}

            {/* ── Content ── */}
            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : sorted.length === 0 ? (
                <div className="crm-inbox-empty">
                    <EmptyStateIcon />
                    <p className="crm-inbox-empty__tagline">All quiet on the western front.</p>
                    <p className="crm-inbox-empty__sub">No {statusFilter !== "all" ? statusFilter : ""} conversations yet.</p>
                </div>
            ) : (
                <div className="crm-inbox-list-wrap">
                    {/* ── Column header ── */}
                    <div className="crm-inbox-list-header">
                        <span className="crm-inbox-col--check">
                            <input
                                type="checkbox"
                                className="crm-inbox-checkbox"
                                checked={allSelected}
                                onChange={handleSelectAll}
                                title="Select all"
                            />
                        </span>
                        <span className="crm-inbox-col--pin" />
                        <span className="crm-inbox-col--lead">Lead</span>
                        <span className="crm-inbox-col--subject">Subject</span>
                        <span className="crm-inbox-col--campaign">Campaign</span>
                        <span className="crm-inbox-col--status">Status</span>
                        <span className="crm-inbox-col--assigned">Assigned</span>
                        <span className="crm-inbox-col--time">Last activity</span>
                        <span className="crm-inbox-col--actions" />
                    </div>

                    {/* ── Rows ── */}
                    <div className="crm-inbox-list">
                        {sorted.map((cv) => {
                            const assignedAdmin = admins?.find(a => a.userId === cv.assignedTo);
                            const isPinned  = Boolean(cv.pinned);
                            const isUnread  = cv.unreadCount > 0;
                            const isDeleting = deleting === cv.id;

                            return (
                                <div
                                    key={cv.id}
                                    className={[
                                        "crm-inbox-row",
                                        isPinned  ? "crm-inbox-row--pinned"  : "",
                                        isUnread  ? "crm-inbox-row--unread"  : "",
                                        selected.has(cv.id) ? "crm-inbox-row--selected" : "",
                                        isDeleting ? "crm-inbox-row--deleting" : "",
                                    ].filter(Boolean).join(" ")}
                                    onClick={() => onOpen(cv.id)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => e.key === "Enter" && onOpen(cv.id)}
                                >
                                    {/* Checkbox */}
                                    <span className="crm-inbox-col--check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            className="crm-inbox-checkbox"
                                            checked={selected.has(cv.id)}
                                            onChange={(e) => handleCheckbox(e, cv.id)}
                                        />
                                    </span>

                                    {/* Pin star */}
                                    <span className="crm-inbox-col--pin">
                                        <button
                                            className={`crm-inbox-pin-btn ${isPinned ? "crm-inbox-pin-btn--active" : ""}`}
                                            onClick={(e) => handleTogglePin(e, cv)}
                                            disabled={pinning === cv.id}
                                            title={isPinned ? "Unpin" : "Pin to top"}
                                        >
                                            ★
                                        </button>
                                    </span>

                                    {/* Lead */}
                                    <span className="crm-inbox-col--lead">
                                        <span className="crm-inbox-lead-name">
                                            {cv.firstName} {cv.lastName}
                                            {isUnread && (
                                                <span className="crm-conv-unread-badge">{cv.unreadCount}</span>
                                            )}
                                        </span>
                                        <span className="crm-inbox-lead-email">{cv.leadEmail}</span>
                                    </span>

                                    {/* Subject + preview */}
                                    <span className="crm-inbox-col--subject">
                                        <span className={`crm-inbox-subject ${isUnread ? "crm-inbox-subject--bold" : ""}`}>
                                            {cv.subject || "(no subject)"}
                                        </span>
                                        {cv.preview && (
                                            <span className="crm-inbox-preview"> — {cv.preview}</span>
                                        )}
                                    </span>

                                    {/* Campaign */}
                                    <span className="crm-inbox-col--campaign">
                                        {cv.campaignName
                                            ? <span className="crm-inbox-campaign-tag">{cv.campaignName}</span>
                                            : <span className="crm-inbox-dash">—</span>
                                        }
                                    </span>

                                    {/* Status */}
                                    <span className="crm-inbox-col--status">
                                        <span className={`crm-status-badge crm-conv-status--${cv.status}`}>
                                            {cv.status}
                                        </span>
                                    </span>

                                    {/* Assigned */}
                                    <span className="crm-inbox-col--assigned crm-inbox-assigned">
                                        {assignedAdmin
                                            ? `${assignedAdmin.firstName} ${assignedAdmin.lastName}`
                                            : cv.assignedTo
                                            ? cv.assignedTo
                                            : <span className="crm-inbox-dash">—</span>
                                        }
                                    </span>

                                    {/* Time */}
                                    <span className="crm-inbox-col--time crm-inbox-time">
                                        {formatTime(cv.lastMessageAt)}
                                    </span>

                                    {/* Row actions */}
                                    <span className="crm-inbox-col--actions crm-inbox-row-actions"
                                        onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className={`crm-inbox-action-btn ${cv.status === "open" ? "crm-inbox-action-btn--close" : "crm-inbox-action-btn--open"}`}
                                            onClick={(e) => handleToggleStatus(e, cv)}
                                            disabled={toggling === cv.id}
                                            title={cv.status === "open" ? "Mark closed" : "Reopen"}
                                        >
                                            {cv.status === "open" ? <CloseConvIcon /> : <ReopenIcon />}
                                        </button>
                                        <button
                                            className="crm-inbox-action-btn crm-inbox-action-btn--delete"
                                            onClick={(e) => handleDelete(e, cv.id)}
                                            disabled={isDeleting}
                                            title="Delete conversation"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConversationInbox;
